import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Edit2, Search, Menu, HelpCircle, HomeIcon, Users, ClipboardList, Eye, Shield, Settings, CheckCircle2, Zap, FileText, Loader2, MessageSquare, MapPin } from 'lucide-react';
export function DecisionPolicy({
  onComplete
}: {
  onComplete?: () => void;
}) {
  const [phase, setPhase] = useState(1);
  const [evalStep, setEvalStep] = useState(-1);
  useEffect(() => {
    let timeouts: NodeJS.Timeout[] = [];
    setEvalStep(-1);
    // Phase 1: Show header only
    // Phase 2: Policy editor slides in after 1 second
    timeouts.push(setTimeout(() => setPhase(2), 1000));
    timeouts.push(setTimeout(() => setPhase(3), 4000)); // Phase 3: Eval start
    // Eval steps (4 rules)
    for (let i = 0; i < 4; i++) {
      timeouts.push(setTimeout(() => setEvalStep(i), 4000 + i * 750));
    }
    timeouts.push(setTimeout(() => setPhase(4), 4000 + 4 * 750 + 1500)); // Phase 4: Result (wait 1.5s after last check)
    // Call onComplete after showing result
    timeouts.push(setTimeout(() => {
      if (onComplete) onComplete();
    }, 4000 + 4 * 750 + 1500 + 5500));
    return () => timeouts.forEach(clearTimeout);
  }, []);
  const policyRules = [{
    label: 'Years in Business',
    value: '3+ years'
  }, {
    label: 'Trade References',
    value: '2 verified'
  }, {
    label: 'Credit Limit',
    value: '≤ $75,000'
  }, {
    label: 'Personal Guarantee',
    value: 'Required'
  }];
  const evalResults = [{
    label: 'Years in Business',
    data: '77 years'
  }, {
    label: 'Trade References',
    data: '2 verified'
  }, {
    label: 'Credit Limit',
    data: '$50,000 requested'
  }, {
    label: 'Personal Guarantee',
    data: 'Signed by Michael Scott'
  }];
  return <div className="w-full h-full bg-white rounded-xl overflow-hidden flex flex-col text-sm border border-gray-200/50" data-id="element-539">
      {/* Product Header */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-200 bg-white shrink-0" data-id="element-540">
        <Menu className="w-4 h-4 text-gray-500" data-id="element-541" />
        <img src="https://cdn.magicpatterns.com/uploads/a5S8ZQgbN9jzaBrUDmr99x/Logo_CreditPulse_Black.png" alt="Credit Pulse" className="h-5 object-contain" data-id="element-542" />
        <div className="flex-1 mx-3 relative" data-id="element-543">
          <input type="text" placeholder="Search Companies" className="w-full pl-3 pr-8 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none" data-id="element-544" />
          <Search className="w-3 h-3 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2" data-id="element-545" />
        </div>
        <HelpCircle className="w-4 h-4 text-gray-400" data-id="element-546" />
        <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[8px] font-bold text-white" data-id="element-547">
          SU
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden" data-id="element-548">
        {/* Left Sidebar */}
        <div className="w-10 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-3.5 shrink-0" data-id="element-549">
          <HomeIcon className="w-4 h-4 text-gray-400" data-id="element-550" />
          <Users className="w-4 h-4 text-gray-400" data-id="element-551" />
          <ClipboardList className="w-4 h-4 text-gray-400" data-id="element-552" />
          <div className="w-7 h-7 rounded-md bg-[#6C5CE7]/10 flex items-center justify-center" data-id="element-553">
            <Eye className="w-4 h-4 text-[#6C5CE7]" data-id="element-554" />
          </div>
          <Shield className="w-4 h-4 text-gray-400" data-id="element-555" />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden" data-id="element-556">
          {/* Company Header */}
          <div className="px-5 py-2 border-b border-gray-200 shrink-0" data-id="element-557">
            <div className="flex items-center mb-1.5" data-id="element-558">
              <div className="flex-1 min-w-0" data-id="element-559">
                <h2 className="text-base font-bold text-gray-900 leading-tight" data-id="element-560">
                  DUNDER MIFFLIN PAPER COMPANY
                </h2>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-561">
                  Credit Manager: Dave Davidson
                </p>
              </div>
              <div className="flex flex-col items-center shrink-0 mx-auto" data-id="element-562">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-563">
                  <span className="text-[10px] font-medium text-gray-500" data-id="element-564">
                    Pulse Score
                  </span>
                  <span className="text-sm font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded" data-id="element-565">
                    82
                  </span>
                </div>
                <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden flex" data-id="element-566">
                  <div className="h-full bg-red-400 w-1/3" data-id="element-567" />
                  <div className="h-full bg-amber-400 w-1/3" data-id="element-568" />
                  <div className="h-full bg-green-500 w-1/3 relative" data-id="element-569">
                    <div className="absolute top-0 bottom-0 w-0.5 bg-gray-900 left-[60%]" data-id="element-570" />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-1.5 shrink-0" data-id="element-571">
                <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-full text-[10px] font-medium text-gray-600" data-id="element-572">
                  <MapPin className="w-3 h-3" data-id="element-573" /> Headquarters
                </span>
                <button className="w-7 h-7 rounded-lg bg-[#6C5CE7]/10 flex items-center justify-center hover:bg-[#6C5CE7]/20 transition-colors" data-id="element-574">
                  <MessageSquare className="w-3.5 h-3.5 text-[#6C5CE7]" data-id="element-575" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 border-b border-gray-200 justify-center" data-id="element-576">
              {['Overview', 'Notifications', 'Details', 'Financials', 'People', 'Applications'].map(tab => <button key={tab} className={`pb-2 text-[10px] font-medium uppercase tracking-wider relative ${tab === 'Overview' ? 'text-[#6C5CE7]' : 'text-gray-500'}`} data-id="element-577">
                  {tab}
                  {tab === 'Overview' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#6C5CE7]" data-id="element-578" />}
                </button>)}
            </div>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col bg-gray-50/30 p-5" data-id="element-579">
            {/* Split Screen Layout */}
            <div className="flex-1 flex gap-4 overflow-hidden min-h-0" data-id="element-580">
              {/* Left: Policy Editor (50%) */}
              <div className="w-1/2 flex flex-col min-h-0" data-id="element-581">
                <AnimatePresence data-id="element-582">
                  {phase >= 2 && <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  type: 'spring',
                  damping: 25,
                  stiffness: 200
                }} className="bg-white border border-gray-200 rounded-xl shadow-sm flex-1 flex flex-col overflow-hidden" data-id="element-583">
                      <div className="px-4 py-2.5 border-b border-gray-100 bg-slate-50 shrink-0 h-[60px] flex flex-col justify-center" data-id="element-584">
                        <div className="flex items-center justify-between mb-0.5" data-id="element-585">
                          <div className="flex items-center gap-2" data-id="element-586">
                            <Settings className="w-4 h-4 text-gray-700" data-id="element-587" />
                            <h3 className="text-base font-bold text-gray-900" data-id="element-588">
                              Company Credit Policy V1
                            </h3>
                            <Edit2 className="w-3.5 h-3.5 text-[#6C5CE7] cursor-pointer ml-1" data-id="element-589" />
                          </div>
                          <span className="px-2.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full uppercase tracking-wider border border-green-200" data-id="element-590">
                            Active
                          </span>
                        </div>
                        <p className="text-xs text-gray-500" data-id="element-591">
                          Active since March 2026
                        </p>
                      </div>

                      <div className="flex-1 flex flex-col" data-id="element-592">
                        {policyRules.map((rule, i) => <div key={i} className="flex items-center justify-between px-5 flex-1 border-b border-gray-100 last:border-0" data-id="element-593">
                            <span className="text-sm font-medium text-gray-800" data-id="element-594">
                              {rule.label}
                            </span>
                            <span className="text-sm font-semibold text-gray-900 bg-gray-50 px-3 py-1 rounded-md" data-id="element-595">
                              {rule.value}
                            </span>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Right: Policy Evaluation (50%) */}
              <div className="w-1/2 flex flex-col min-h-0" data-id="element-596">
                <AnimatePresence data-id="element-597">
                  {phase >= 3 && <motion.div initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  type: 'spring',
                  damping: 25,
                  stiffness: 200
                }} className="bg-[#6C5CE7]/[0.03] border border-[#6C5CE7]/20 rounded-xl flex-1 flex flex-col overflow-hidden relative" data-id="element-598">
                      <div className="px-4 py-2.5 border-b border-[#6C5CE7]/10 bg-white/50 shrink-0 h-[60px] flex flex-col justify-center" data-id="element-599">
                        <div className="flex items-center gap-2 mb-0.5" data-id="element-600">
                          <Zap className="w-4 h-4 text-[#6C5CE7]" data-id="element-601" />
                          <h3 className="text-base font-bold text-gray-900" data-id="element-602">
                            Evaluating: DUNDER MIFFLIN
                          </h3>
                          {phase < 4 && <span className="relative flex h-2.5 w-2.5 ml-1" data-id="element-603">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6C5CE7] opacity-75" data-id="element-604"></span>
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#6C5CE7]" data-id="element-605"></span>
                            </span>}
                        </div>
                        <p className="text-xs text-gray-500" data-id="element-606">
                          Running policy against enriched data...
                        </p>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full h-1.5 bg-gray-200 overflow-hidden shrink-0" data-id="element-607">
                        <motion.div className="h-full bg-gradient-to-r from-[#6C5CE7] to-green-500" initial={{
                      width: '0%'
                    }} animate={{
                      width: phase >= 4 ? '100%' : `${(evalStep + 1) / 6 * 100}%`
                    }} transition={{
                      duration: 0.3
                    }} data-id="element-608" />
                      </div>

                      <div className="flex-1 flex flex-col relative" data-id="element-609">
                        {evalResults.map((result, i) => {
                      const isVisible = evalStep >= i || phase >= 4;
                      const isEvaluating = evalStep === i && phase === 3;
                      const isPassed = evalStep > i || phase >= 4;
                      return <div key={i} className={`flex items-center justify-between px-5 flex-1 border-b border-gray-100/50 last:border-0 transition-all duration-300 ${isVisible ? isPassed ? 'border-l-2 border-l-green-500' : 'border-l-2 border-l-[#6C5CE7]' : 'border-l-2 border-l-transparent'}`} style={{
                        opacity: isVisible ? 1 : 0
                      }} data-id="element-610">
                              <div className="flex items-center gap-2" data-id="element-611">
                                <span className="text-sm font-medium text-gray-800" data-id="element-612">
                                  {result.label}
                                </span>
                                <span className="text-xs text-gray-400" data-id="element-613">—</span>
                                <span className="text-sm text-gray-600" data-id="element-614">
                                  {result.data}
                                </span>
                              </div>
                              <div className="shrink-0" data-id="element-615">
                                {isEvaluating ? <Loader2 className="w-4 h-4 text-[#6C5CE7] animate-spin" data-id="element-616" /> : isPassed ? <motion.div initial={{
                            scale: 0
                          }} animate={{
                            scale: 1
                          }} transition={{
                            type: 'spring',
                            damping: 20,
                            stiffness: 300
                          }} className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-full" data-id="element-617">
                                    <CheckCircle2 className="w-4 h-4" data-id="element-618" />
                                    <span className="text-xs font-bold uppercase" data-id="element-619">
                                      Pass
                                    </span>
                                  </motion.div> : null}
                              </div>
                            </div>;
                    })}

                        {/* Recommendation card overlay at phase 4 */}
                        <AnimatePresence data-id="element-620">
                          {phase >= 4 && <motion.div key="ready-card" initial={{
                        opacity: 0,
                        scale: 0.85,
                        y: 20
                      }} animate={{
                        opacity: 1,
                        scale: 1,
                        y: 0
                      }} transition={{
                        type: 'spring',
                        damping: 20,
                        stiffness: 300,
                        delay: 0.1
                      }} className="absolute bottom-3 left-4 right-4 bg-white border-2 border-green-400 shadow-[0_8px_40px_rgba(34,197,94,0.2)] rounded-xl px-4 py-3 z-10" data-id="element-621">
                              <div className="flex flex-col items-center gap-2" data-id="element-622">
                                <CheckCircle2 className="w-6 h-6 text-green-500" data-id="element-623" />
                                <span className="text-sm font-bold text-gray-900" data-id="element-624">
                                  Recommendation Ready
                                </span>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}